import React, { useEffect, useRef, useState } from 'react';
import { IoPlayOutline } from 'react-icons/io5';
import image1 from '../../../assets/Image1.png';
import DateIcon from '../../../assets/DateIcon.svg';
import { useTranslation } from 'react-i18next';
import axiosVendorInstance from '../../../axiosVendorInstance';
import Order from '../../../assets/svg/delivery-icon/Order';
import Preparing from '../../../assets/svg/delivery-icon/Preparing';
import InTransit from '../../../assets/svg/delivery-icon/InTransit';
import Delivery from '../../../assets/svg/delivery-icon/Delivery';

const OrderInfo = ({
    setIsLoading,
    orderInvoiceData,
    setOrderInvoiceData,
    id,
    vendor = false,
}) => {

    const cleanedVendorAddress =
    orderInvoiceData?.Restaurant_Details?.Restaurant_Address?.trim().replace(
        /,+\s*$/,
        ''
    );
    const vendorAddress = orderInvoiceData?.Restaurant_Details
        ? `${cleanedVendorAddress}, ${orderInvoiceData?.Restaurant_Details?.Restaurant_City}, ${orderInvoiceData?.Restaurant_Details?.Restaurant_State}, ${orderInvoiceData?.Restaurant_Details?.Restaurant_Pincode}`
        : '';

    const cleanedBuyerAddress =
    orderInvoiceData?.Buyer_Details?.Address?.trim().replace(/,+\s*$/, '');
    const buyerAddress = orderInvoiceData?.Buyer_Details
        ? `${cleanedBuyerAddress}, ${orderInvoiceData?.Buyer_Details?.City}, ${orderInvoiceData?.Buyer_Details?.State}, ${orderInvoiceData?.Buyer_Details?.Pincode}`
        : '';
    const invoiceRef = useRef(null);
    const { t } = useTranslation();
    const videoRefs = useRef({});
    const [playingIndex, setPlayingIndex] = useState(null);

    const handlePlay = (e, index) => {
        e.stopPropagation();

        Object.entries(videoRefs.current).forEach(([key, video]) => {
            if (parseInt(key) !== index && video && !video.paused) {
                video.pause();
            }
        });

        const currentVideo = videoRefs.current[index];
        if (currentVideo) {
            currentVideo.play();
            setPlayingIndex(index);
        }
    };

    const fetchInvoiceData = async () => {
        try {
            setIsLoading(true);
            const response = await axiosVendorInstance.get(
                `/Order/user/orderinvoice/${id}`,
                { skipAuth: true }
            );
            const invoiceData = response?.data?.invoice?.[0]?.['0']?.Invoice_Details;

            if (!invoiceData) {
                console.warn('Invoice data is missing or invalid:', response?.data);
                setOrderInvoiceData(null); // Set to null to prevent crashes
                setIsLoading(false);
                return;
            }

            setOrderInvoiceData(invoiceData);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error('Error fetching order invoice:', error);
            setOrderInvoiceData(null);
        }
    };

    useEffect(() => {
        fetchInvoiceData();

        const interval = setInterval(() => {
            fetchInvoiceData();
        }, 10000);

        return () => clearInterval(interval);
    }, []);

    function formatDate(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date)) return '';

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${day}-${month}-${year}`;
    }

    const total = orderInvoiceData?.Order_Details?.reduce(
        (sum, item) => sum + (item?.Menu_Item_Price * item?.Quantity || 0),
        0
    )?.toFixed(2);

    function getStatusInfo(status) {
        if (status === 'out_for_delivery' && orderInvoiceData?.Buyer_Details?.Delivery_Status === 'pickup') {
            return {
                label: 'Ready for Pickup',
                className: 'status-out-for-delivery',
            };
        }
        switch (status) {
        case 'order_placed':
            return {
                label: 'Order Placed',
                className: 'status-placed',
            };
        case 'order_accepted':
            return {
                label: 'Order Preparing',
                className: 'status-preparing',
            };
        case 'order_rejected':
            return {
                label: 'Order Rejected',
                className: 'status-rejected',
            };
        case 'cancelled':
            return {
                label: 'Order Cancelled',
                className: 'status-cancelled',
            };
        case 'out_for_delivery':
            return {
                label: 'Order Preparing',
                className: 'status-preparing',
            };
        case 'In_Transit':
            return {
                label: 'Out for Delivery',
                className: 'status-out-for-delivery',
            };
        case 'delivered':
            return {
                label: 'Delivered',
                className: 'status-delivered',
            };
        default:
            return {
                label: 'Unknown Status',
            };
        }
    }

    const statusInfo = getStatusInfo(orderInvoiceData?.Status_Text);

    const statusStages = [
        'order_placed',
        'order_accepted',
        'out_for_delivery',
        'In_Transit',
        'delivered',
    ];

    const getAdjustedStatusIndex = (currentStatus) => {
        let index = statusStages.indexOf(currentStatus);
        if (
            orderInvoiceData?.Buyer_Details?.Delivery_Status === 'pickup' &&
            currentStatus === 'out_for_delivery'
        ) {
            index += 1;
        }
        return index;
    };


    const getStageColors = (currentStatus, stage) => {
        const currentIndex = getAdjustedStatusIndex(currentStatus);
        const stageIndex = statusStages.indexOf(stage);
    
        if (currentStatus === 'delivered') {
            return {
                fillColor: '#4CAF50',
                strokeColor: '#4CAF50',
                isCompleted: true,
                isCurrent: false,
            };
        }
        
        if (stageIndex < currentIndex) {
            return {
                fillColor: '#4CAF50',
                strokeColor: '#4CAF50',
                isCompleted: true,
                isCurrent: false,
            };
        }

        if (currentStatus === 'order_rejected') {
            if (stage === 'order_placed') {
                return {
                    fillColor: '#4CAF50',
                    strokeColor: '#4CAF50',
                    isCompleted: true,
                    isCurrent: false,
                };
            }
        
            if (stage === 'order_rejected') {
                return {
                    fillColor: '#E13535',
                    strokeColor: '',
                    isCompleted: false,
                    isCurrent: true,
                };
            }
        
            return {
                fillColor: '#ccc',
                strokeColor: '#ccc',
                isCompleted: false,
                isCurrent: false,
            };
        }
        
        return {
            fillColor: '#E13535',
            strokeColor: undefined,
            isCompleted: false,
            isCurrent: false,
        };
    };

    const getLineClass = (currentStatus, lineStage) => {
        const currentIndex = getAdjustedStatusIndex(currentStatus);
        const stageIndex = statusStages.indexOf(lineStage);

        if (currentStatus === 'delivered') {
            return 'line green';
        }
    
        return stageIndex <= currentIndex ? 'line green' : 'line red';
    };
    
    const isRejected = orderInvoiceData?.Status_Text === 'order_rejected';

    const isPickupInTransit =
    orderInvoiceData?.Buyer_Details?.Delivery_Status === 'pickup' &&
    orderInvoiceData?.Status_Text === 'out_for_delivery';

    const normalizedStatus = isPickupInTransit ? 'In_Transit' : orderInvoiceData?.Status_Text;

    return (
        <div ref={invoiceRef} className="order-details-part1">
            <div className="order-card1">
                <div className="card-title">
                    <h2 className="order-id">Order ID : {orderInvoiceData?.Order_ID}</h2>
                    {statusInfo && (
                        <div className={`status-pill-icon ${statusInfo.className}`}>
                            {statusInfo.label}
                        </div>
                    )}

                    <div className="order-date-container">
                        <img src={DateIcon} alt="date" />
                        <p className="order-date">
                            {formatDate(orderInvoiceData?.Order_Date)}
                        </p>
                    </div>
                </div>
                <div className="break-line11"></div>
                <div className="order-basic-information">
                    {!vendor && (
                        <>
                            <div className="restaurant-details-container">
                                <div className="Heading-customer-vendor">
                                    Restaurant Info
                                </div>
                                <div className="info-container">
                                    <p className="info-text">Restaurant</p>
                                    <span className="two-dot">:</span>
                                    <p className="info-value">
                                        {orderInvoiceData?.Restaurant_Details?.Restaurant_Name}
                                    </p>
                                </div>
                                <div className="info-container">
                                    <p className="info-text">Address</p>
                                    <span className="two-dot">:</span>
                                    <p className="info-value">{vendorAddress}</p>
                                </div>
                                <div className="info-container">
                                    <p className="info-text">Contact</p>
                                    <span className="two-dot">:</span>
                                    <p className="info-value">
                                        {orderInvoiceData?.Restaurant_Details?.Restaurant_Phone}
                                    </p>
                                </div>
                            </div>
                            <div className="vertical-line-box">
                                <div className="vertical-line1"></div>
                            </div>
                        </>
                    )}
                    <div className="customer-details-container">
                        <div className="Heading-customer-vendor">
                            Customer Info
                        </div>
                        <div className="info-container">
                            <p className="info-text">Customer</p>
                            <span className="two-dot">:</span>
                            <p className="info-value">
                                {orderInvoiceData?.Buyer_Details?.First_Name}{' '}
                                {orderInvoiceData?.Buyer_Details?.Last_Name}
                            </p>
                        </div>
                        {orderInvoiceData?.Buyer_Details?.Address && (     
                            <div className="info-container">
                                <p className="info-text">Address</p>
                                <span className="two-dot">:</span>
                                <p className="info-value">{buyerAddress}</p>
                            </div>
                        )}
                        <div className="info-container">
                            <p className="info-text">Contact</p>
                            <span className="two-dot">:</span>
                            <p className="info-value">
                                {orderInvoiceData?.Buyer_Details?.Mobile}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {isRejected ? (
                <div className="order-card1">
                    <div className="order-preparing-intransit-delivery" style={{width:'50%'}}>
                        <div className="status">
                            <Order
                                {...getStageColors(orderInvoiceData?.Status_Text, 'order_placed')}
                            />
                        </div>
                        <div className={getLineClass(orderInvoiceData?.Status_Text,'order_rejected')}></div>
                        <div className="status">
                            {orderInvoiceData?.Status_Text === 'order_rejected' && (
                                <div className="status-1"></div>
                            )}
                            <Delivery
                                {...getStageColors(orderInvoiceData?.Status_Text, 'order_rejected')}
                            />
                        </div>
                    </div>
                    <div className="order-preparing-intransit-delivery-text" style={{width:'50%'}}>
                        <p className='delivery-icon-text-name order'>Order</p>
                        <p className='delivery-icon-text-name delivered'>Rejected</p>
                    </div>
                </div>
            ) : (
                <div className="order-card1">
                    <div className="order-preparing-intransit-delivery">
                        <div className="status">
                            {orderInvoiceData?.Status_Text === 'order_placed' && (
                                <div className="status-1"></div>
                            )}
                            <Order
                                {...getStageColors(orderInvoiceData?.Status_Text, 'order_placed')}
                            />
                        </div>
                        <div className={getLineClass(orderInvoiceData?.Status_Text, 'order_accepted')}></div>
                        <div className="status">
                            {(orderInvoiceData?.Status_Text === 'order_accepted' || (orderInvoiceData?.Status_Text === 'out_for_delivery' && orderInvoiceData?.Buyer_Details?.Delivery_Status === 'delivery')) && (
                                <div className="status-1"></div>
                            )}
                            {orderInvoiceData?.Buyer_Details?.Delivery_Status === 'delivery'?(
                                <Preparing
                                    {...getStageColors(
                                        orderInvoiceData?.Status_Text === 'out_for_delivery'
                                            ? 'order_accepted'
                                            : orderInvoiceData?.Status_Text,
                                        'order_accepted'
                                    )}
                                />
                            ):(
                                <Preparing
                                    {...getStageColors(
                                        orderInvoiceData?.Status_Text === 'In_Transit'
                                            ? 'order_accepted'
                                            : orderInvoiceData?.Status_Text,
                                        'order_accepted'
                                    )}
                                />
                            )}
                        </div>
                        <div className={getLineClass(orderInvoiceData?.Status_Text, 'In_Transit')}></div>
                        <div className="status">
                            {(orderInvoiceData?.Status_Text === 'In_Transit' || normalizedStatus === 'In_Transit') && (
                                <div className="status-1"></div>
                            )}
                            <InTransit
                                {...getStageColors(orderInvoiceData?.Status_Text, 'In_Transit')}
                            />
                        </div>
                        <div className={getLineClass(orderInvoiceData?.Status_Text, 'delivered')}></div>

                        <div className="status">
                            <Delivery
                                {...getStageColors(orderInvoiceData?.Status_Text, 'delivered')}
                            />
                        </div>
                    </div>
                    <div className="order-preparing-intransit-delivery-text">
                        <p className='delivery-icon-text-name order'>Order</p>
                        <p className='delivery-icon-text-name preparing'>Preparing</p>
                        <p className='delivery-icon-text-name in-transit'> {orderInvoiceData?.Buyer_Details?.Delivery_Status === 'pickup' ? 'Ready for Pickup' : 'In-Transit'} </p>
                        <p className='delivery-icon-text-name delivered'>Delivered</p>
                    </div>
                </div>
            )}

            {orderInvoiceData?.Order_Details[0]?.Instructions && (
                <div className="order-card1">
                    <div className="order-basic-information">
                        <div className="customer-details-container-1">
                            <div className="info-container">
                                <p className="info-text">Instruction</p>
                                <span className="two-dot">:</span>
                                <p className="info-value">
                                    {orderInvoiceData?.Order_Details[0]?.Instructions}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="items">
                <div className="item-title">Item</div>
                <div className="break-line11"></div>
                {orderInvoiceData?.Order_Details?.map((item, index) => (
                    <div key={index} className="item-card">
                        <div className="item-image-name-container">
                            <div className="order-details-media-wrapper">
                                {item?.Menu_Item_Image?.match(/\.(mp4|webm|ogg)$/i) ? (
                                    <div className="order-details-video-container1">
                                        <video
                                            src={item?.Menu_Item_Image}
                                            muted
                                            playsInline
                                            // eslint-disable-next-line react/no-unknown-property
                                            webkit-playsinline='true'
                                            preload="metadata"
                                            className="order-details-food-video"
                                            ref={(el) => (videoRefs.current[index] = el)}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const video = videoRefs.current[index];
                                                if (video && !video.paused) {
                                                    video.pause();
                                                    setPlayingIndex(null);
                                                }
                                            }}
                                            onEnded={() => setPlayingIndex(null)}
                                            onError={(e) => {
                                                e.target.poster = image1;
                                            }}
                                        />
                                        {playingIndex !== index && (
                                            <button
                                                onClick={(e) => handlePlay(e, index)}
                                                className="play-btn"
                                            >
                                                <IoPlayOutline color="white" fontSize={34} />
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <img
                                        className="order-details-Food-image"
                                        src={item?.Menu_Item_Image || image1}
                                        alt="Food img"
                                        onError={(e) => {
                                            e.target.src = image1;
                                        }}
                                    />
                                )}
                            </div>
                            <div className="order-item-details">
                                <div className="order-details1">
                                    <p className="item-name">{item?.Menu_Item_Name}</p>
                                    <p
                                        className="description-value"
                                        dangerouslySetInnerHTML={{
                                            __html:
                        item?.Description?.length > 60
                            ? item?.Description.slice(0, 60) + '...'
                            : item?.Description,
                                        }}
                                    />
                                    <div className="break-line-11"></div>
                                    <div className="price-quantity-container">
                                        <div className="item-prize-info-container">
                                            <p className="item-info-text">
                                                {t('orderDetails.quantity')} :
                                            </p>
                                            <p className="item-info-value">{item?.Quantity}</p>
                                        </div>
                                        <div className="item-prize-info-container">
                                            <p className="item-info-text">
                                                {t('orderDetails.Price')} :
                                            </p>
                                            <p className="item-info-value">
                        ${item?.Menu_Item_Price?.toFixed(2)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="total-order-prize">
                                    <p className="item-total-price">
                    ${(item?.Menu_Item_Price * item?.Quantity)?.toFixed(2)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="prize-calculation">
                <div className="price-total">
                    <div className="items-total">Sub Total</div>
                    <div className="items-total">${total}</div>
                </div>
                <div className="break-line11"></div>
                <div className="item-total-prize1">
                    <div className="summary-card">
                        {orderInvoiceData?.Order_Discount > 0 &&
                            <div className="summary-item">
                                <p className="heading">{t('orderDetails.discount')} :</p>
                                <p className="value">
                                    {orderInvoiceData?.Order_Discount ? `$${orderInvoiceData?.Order_Discount?.toFixed(2)}` : '$0.00'}
                                </p>
                            </div>
                        }
                        <div className="summary-item">
                            <p className="heading">{t('orderDetails.Taxes-Other-Fees')} :</p>
                            <p className="value">+${orderInvoiceData?.OrderTaxesAndOtherFees?.toFixed(2) || 0}</p>
                        </div>
                        {orderInvoiceData?.Buyer_Details?.Delivery_Status !== 'pickup' &&
                            <div className="summary-item">
                                <p className="heading">{t('orderDetails.delivery-charge')} :</p>
                                <p className="value">+${orderInvoiceData?.Order_Delivery_Fee || 0}</p>
                            </div>
                        }
                    </div>
                </div>

                <div className="summary-item-total">
                    <p className="summary-total-heading">
                        {t('orderDetails.final-total')} :
                    </p>
                    <p className="summary-total-value">
            ${orderInvoiceData?.OrderFinalPrice?.toFixed(2)}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default OrderInfo;
